import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Callout, Card, CardContent, CardHeader, KeyValueList } from './ui'
import { leadInboxApi, type ContractLeadOrigin as Origin } from '../api/leadInbox'

// F-110 (REQ-062-003): provenienza della pratica = il lead da cui nasce (fonte, campagna con ad set e creatività,
// sottocampagna, canale, partner, landing, UTM, fornitore, data di acquisizione). Si nasconde da sola se la pratica
// non ha nessun lead. Nessun importo.

/** Una riga del dettaglio: senza valore non compare. */
function rows(entries: [string, string | null | undefined][]): { label: string; value: string }[] {
  return entries.flatMap(([label, value]) => (value ? [{ label, value }] : []))
}

export function ContractLeadOrigin({ contractId }: { contractId: number }) {
  const [origin, setOrigin] = useState<Origin | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    setOrigin(null)
    setError(null)
    leadInboxApi.origin(contractId)
      .then(r => { if (alive) setOrigin(r ?? null) })
      .catch(e => { if (alive) setError(e instanceof Error ? e.message : 'Errore nel caricamento della provenienza') })
    return () => { alive = false }
  }, [contractId])

  if (error) return <Callout tone="warning">{error}</Callout>
  if (!origin) return null

  const utm = origin.utm
  const utmText = [
    utm.source && `source=${utm.source}`,
    utm.medium && `medium=${utm.medium}`,
    utm.campaign && `campaign=${utm.campaign}`,
    utm.content && `content=${utm.content}`,
    utm.term && `term=${utm.term}`,
  ].filter(Boolean).join(' · ')

  return (
    <Card variant="outlined" className="mb-3" data-testid="contract-lead-origin">
      <CardHeader
        title="Provenienza"
        actions={
          <Link to={`/leads/${origin.leadId}`} className="md-typescale-body-small text-decoration-none">
            Lead #{origin.leadId}
          </Link>
        }
      />
      <CardContent>
        <KeyValueList items={rows([
          ['Fonte', origin.sourceLabel ?? origin.source],
          ['Campagna', origin.campaign?.name],
          ['Ad set', origin.campaign?.adSet],
          ['Creatività', origin.campaign?.creative],
          ['Sottocampagna', origin.subCampaign],
          ['Canale', origin.channel],
          ['Partner', origin.partner],
          ['Fornitore', origin.provider?.name],
          ['Landing page', origin.landingPage],
          ['Parametri UTM', utmText],
          ['Acquisito il', new Date(origin.acquiredAt).toLocaleString('it-IT', {
            day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
          })],
        ])} />
        <div className="md-typescale-body-small text-muted mt-2">
          {origin.resolvedVia === 'converted'
            ? 'Lead convertito in questa pratica.'
            : 'Lead dell’appuntamento collegato a questa pratica.'}
        </div>
      </CardContent>
    </Card>
  )
}
